import {useContext, useEffect, useState} from "react";
import {Box, Button, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {SignInContext} from "../../../context/SignInContext";
import axios from "../../../config/axios";
import {c, mono, maxW, primaryButtonSx} from "../landingTokens";

const COUNTRY_BY_LANGUAGE = {
    en: "gb",
    mk: "mk",
    sr: "rs",
    hr: "hr",
    sq: "al",
    al: "al",
    pl: "pl",
    uk: "ua",
};

const REDIRECT_MESSAGES_BY_LANGUAGE = {
    en: "Your call will be redirected. Thank you!",
    mk: "Вашиот повик ќе биде пренасочен. Ви благодариме!",
    sr: "Vaš poziv će biti preusmeren. Hvala vam!",
    hr: "Vaš poziv će biti preusmjeren. Hvala vam!",
    sq: "Thirrja juaj do të ridrejtohet. Faleminderit!",
    al: "Thirrja juaj do të ridrejtohet. Faleminderit!",
    pl: "Twoje połączenie zostanie przekierowane. Dziękujemy!",
    uk: "Ваш дзвінок буде перенаправлено. Дякуємо!",
};

const PROMPT_EXAMPLES = [
    "You are a helpful customer support agent for Acme Corp. Handle billing inquiries, returns, and general product questions.",
    "You are a clinic receptionist. Book, move and cancel appointments, and confirm insurance details.",
    "You are a friendly sales assistant. Qualify callers, answer questions about our plans, and book a demo.",
];

export const AgentBuilder = ({heroArtifact = false, embedded = false}) => {
    const navigate = useNavigate();
    const {currentUser} = useContext(SignInContext);
    const [prompt, setPrompt] = useState("");
    const [languages, setLanguages] = useState([]);
    const [language, setLanguage] = useState("");
    const [placeholder, setPlaceholder] = useState("");

    useEffect(() => {
        let active = true;

        axios.get("/api/language-support/all")
            .then((response) => {
                if (!active) return;
                const supportedLanguages = Array.isArray(response?.data) ? response.data : [];
                setLanguages(supportedLanguages);
                setLanguage((current) => current || supportedLanguages[1]?.code || "");
            })
            .catch((error) => {
                console.error("Failed to load supported languages:", error);
            });

        return () => {
            active = false;
        };
    }, [currentUser]);

    useEffect(() => {
        if (prompt.trim()) return undefined;

        let exampleIndex = 0;
        let characterIndex = 0;
        let deleting = false;
        let pauseTicks = 0;

        const timer = window.setInterval(() => {
            const example = PROMPT_EXAMPLES[exampleIndex];

            if (pauseTicks > 0) {
                pauseTicks -= 1;
                return;
            }

            if (!deleting) {
                characterIndex += 1;
                setPlaceholder(example.slice(0, characterIndex));
                if (characterIndex === example.length) {
                    deleting = true;
                    pauseTicks = 22;
                }
                return;
            }

            characterIndex -= 1;
            setPlaceholder(example.slice(0, characterIndex));
            if (characterIndex === 0) {
                deleting = false;
                exampleIndex = (exampleIndex + 1) % PROMPT_EXAMPLES.length;
                pauseTicks = 5;
            }
        }, 42);

        return () => window.clearInterval(timer);
    }, [prompt]);

    const handleGenerate = () => {
        const selectedLanguage = languages.find(
            (item) => item.code === language
        );

        if (!selectedLanguage) return;

        const languageKey = (
            selectedLanguage.lang || selectedLanguage.code
        )
            .split("-")[0]
            .toLowerCase();

        const redirectMessage =
            REDIRECT_MESSAGES_BY_LANGUAGE[languageKey] ||
            REDIRECT_MESSAGES_BY_LANGUAGE.en;

        sessionStorage.setItem(
            "openbrainAgentLanguage",
            JSON.stringify(selectedLanguage)
        );

        sessionStorage.setItem("redirect_message", redirectMessage);
        sessionStorage.setItem("openbrainAgentPrompt", prompt.trim());

        navigate(currentUser ? "/onboarding" : "/login?next=/onboarding");
    };

    return (
        <Box component="section" id="builder" data-screen-label="Agent Builder" sx={{
            position: "relative",
            zIndex: 1,
            mt: embedded ? 0 : (heroArtifact ? {xs: 4, md: 5} : {xs: 6, md: 8}),
            px: 0,
            pt: 0,
            pb: embedded ? 0 : (heroArtifact ? 0 : {xs: 8, md: 13}),
            background: "transparent"
        }}>
            <Box sx={{maxWidth: embedded ? "100%" : (heroArtifact ? 1120 : maxW), mx: "auto", width: "100%"}}>
                {!heroArtifact && !embedded && (
                    <Box sx={{mb: 5.5, maxWidth: 700}}>
                        <Typography sx={{
                            fontFamily: mono,
                            fontSize: 11.5,
                            letterSpacing: ".16em",
                            textTransform: "uppercase",
                            color: c.purpleDeep,
                            mb: 1.5
                        }}>Agent Builder</Typography>
                        <Typography component="h2" sx={{
                            fontSize: "clamp(32px, 4.5vw, 52px)",
                            lineHeight: 1.04,
                            letterSpacing: "-.04em",
                            fontWeight: 800,
                            color: c.ink
                        }}>Configure your agent<br/>in plain language</Typography>
                    </Box>
                )}

                <Box sx={{
                    position: "relative",
                    borderRadius: embedded ? "18px" : {xs: "24px", md: "30px"},
                    bgcolor: embedded ? "#F9FAFC" : c.white,
                    overflow: "hidden",
                    boxShadow: embedded ? "0 14px 34px -24px rgba(66,46,170,0.45)" : "0 50px 120px -50px rgba(20,21,26,.38)"
                }}>
                    <Stack direction="row" alignItems="center" gap={.875}
                           sx={{height: 46, px: {xs: 2, md: 2.75}, bgcolor: "rgba(250,250,251,.88)"}}>
                        {[0, 1, 2].map((i) => <Box key={i} sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#D1D2D8"
                        }}/>)}
                        <Typography sx={{fontFamily: mono, fontSize: 10.5, color: c.inkMuted, ml: 1}}>openbrain · agent
                            builder</Typography>
                    </Stack>

                    <Box sx={{p: embedded ? {xs: 2.25, md: 2.5} : {xs: 3, md: 4.5}}}>
                        <Typography sx={{
                            fontFamily: mono,
                            fontSize: 11,
                            letterSpacing: ".14em",
                            textTransform: "uppercase",
                            color: c.purpleDeep,
                            mb: .75
                        }}>Agent prompt</Typography>
                        {!embedded && (
                            <Typography sx={{fontSize: 20, fontWeight: 700, color: c.ink, mb: 2.5}}>Describe your
                                agent</Typography>
                        )}
                        <TextField
                            fullWidth
                            multiline
                            minRows={embedded ? 5 : 8}
                            value={prompt}
                            onChange={(event) => {
                                if (event.target.value.length <= 300) {
                                    setPrompt(event.target.value);
                                }
                            }}
                            placeholder={placeholder}
                            inputProps={{
                                maxLength: 300,
                            }}
                            helperText={`${prompt.length}/300`}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "18px",
                                    fontSize: 14,
                                    lineHeight: 1.6,
                                    alignItems: "flex-start",
                                    bgcolor: "rgba(246,247,253,.82)",
                                    transition: "background-color 350ms cubic-bezier(.16,1,.3,1), box-shadow 350ms cubic-bezier(.16,1,.3,1)",
                                    "& fieldset": {border: "none"},
                                    "&:hover": {bgcolor: "rgba(242,243,251,.96)"},
                                    "&.Mui-focused": {
                                        bgcolor: c.white,
                                        boxShadow: "0 14px 34px -26px rgba(79,70,229,.55)"
                                    },
                                    "&.Mui-focused fieldset": {border: "none"},
                                },
                            }}
                        />
                        <Stack direction="row" gap={1.25} alignItems="stretch" sx={{mt: 2.5}}>
                            <TextField
                                select
                                size="small"
                                value={language}
                                onChange={(event) => setLanguage(event.target.value)}
                                SelectProps={{
                                    renderValue: (value) => {
                                        const selected = languages.find((item) => item.code === value);
                                        if (!selected) return "Select language";
                                        const country = selected.country || COUNTRY_BY_LANGUAGE[selected.code?.split("-")[0]?.toLowerCase()];
                                        return (
                                            <Stack direction="row" alignItems="center" gap={.75}>
                                                {country && <Box component="img"
                                                                 src={`https://flagcdn.com/24x18/${country}.png`} alt=""
                                                                 sx={{
                                                                     width: 20,
                                                                     height: 15,
                                                                     objectFit: "cover",
                                                                     borderRadius: "3px"
                                                                 }}/>}
                                                {selected.language || selected.label || selected.code}
                                            </Stack>
                                        );
                                    },
                                }}
                                sx={{
                                    width: {xs: 155, sm: 175},
                                    flexShrink: 0,
                                    "& .MuiOutlinedInput-root": {
                                        height: 48,
                                        borderRadius: "12px",
                                        bgcolor: "#F9FAFB",
                                        fontSize: 12,
                                        "& fieldset": {borderColor: c.line}
                                    }
                                }}
                            >
                                {languages.map((item) => {
                                    const country = item.country || COUNTRY_BY_LANGUAGE[item.code?.split("-")[0]?.toLowerCase()];
                                    return (
                                        <MenuItem key={item.id || item.code} value={item.code}
                                                  sx={{fontSize: 12, gap: 1}}>
                                            {country &&
                                                <Box component="img" src={`https://flagcdn.com/24x18/${country}.png`}
                                                     alt="" sx={{
                                                    width: 20,
                                                    height: 15,
                                                    objectFit: "cover",
                                                    borderRadius: "3px"
                                                }}/>}
                                            {item.language || item.label || item.code}
                                            <Typography component="span" sx={{
                                                ml: "auto",
                                                fontFamily: mono,
                                                fontSize: 10,
                                                color: c.inkMuted
                                            }}>{item.code}</Typography>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                            <Button fullWidth disabled={!prompt.trim()} onClick={handleGenerate} sx={{
                                ...primaryButtonSx,
                                minWidth: 0,
                                py: 1.4,
                                borderRadius: "12px",
                                fontWeight: 700
                            }}>Generate agent</Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AgentBuilder;
